import React, { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import type { EditorActions } from '@atlaskit/editor-core';
import Link from '@atlaskit/link/link';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	AddItemButton,
	ConfigPanelFeedbackCollector,
	ConfigPanelGrouping,
	ExtensionsConfigWrapper,
	FeedbackType,
	FormFieldErrorMessage,
	NoExtensionData,
	PremiumGateForExtensionConfigPanel,
	useExtensionsActions,
	useIsDisabledContext,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { useExtensionsState } from '@atlassian/company-hub-common/controllers/use-extensions-state';
import {
	ManageTranslations,
	ManageTranslationsOpenProvider,
	useManageTranslationsOpen,
} from '@atlassian/company-hub-common/manage-translations';
import { useIsTranslationSupportEnabled } from '@atlassian/company-hub-common/use-is-translation-support-enabled';

import {
	carouselConfigAnalyticSource,
	carouselContentAnalyticSource,
	carouselExtensionKey,
	carouselExtensionType,
	FRAMES_LIMIT_V2,
} from '../../constants';
import { useCarouselExtensionParametersAndActions } from '../../controllers/use-carousel-extension-parameters-and-actions';
import type { Frame } from '../../types';
import { FramesArea } from './content-section/frames-area';
import { messages as frameMessages } from './content-section/frames-area/frame-content/frame-field/messages';
import { messages } from './messages';
import { CarouselConfigStylesSectionGeneral } from './styles-section/general';
import { CarouselConfigStylesSectionNavigation } from './styles-section/navigation';

const SUPPORT_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/add-a-trio-of-dynamic-frames-to-your-page-with-carousel/';

const TRIGGER_BUTTON_KEYS = ['Enter', 'Spacebar', ' '];

enum TabValues {
	STYLES = 'styles',
	CONTENT = 'content',
}

const styles = cssMap({
	carouselListBuilderFooterWrapper: {
		paddingBlock: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	stickyFooter: {
		paddingInline: token('space.050'),
		marginInline: token('space.negative.050'),
		position: 'sticky',
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
	},
	disabledContentWrapper: {
		cursor: 'not-allowed',
	},
	supportLinkWrapper: {
		marginBottom: token('space.negative.100'),
	},
});

export type CarouselConfigPanelContentProps = {
	closeConfigPanel: () => void;
	contentId: string;
	editorActions: EditorActions;
	localId: string;
};

export const CarouselConfigPanelContentInner = ({
	contentId,
	closeConfigPanel,
	localId,
	editorActions,
}: CarouselConfigPanelContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { isOpen: isManageTranslationsOpen } = useManageTranslationsOpen();
	const { carouselActions, carouselParameters } = useCarouselExtensionParametersAndActions({
		localId,
		editorActions,
	});

	const framesListRef = useRef<HTMLDivElement>(null);
	const {
		cards: frames,
		isAutoplayEnabled,
		cornerStyle,
		cornerRadius,
		navigationStyle,
	} = carouselParameters || {};
	const { addFrame, removeFrame, updateFrameFormField, updateMainFormField } = carouselActions;
	const { isCompanyHub, showConfigPreambleSupportLink } = useProductDependencies();
	const isDisabled = useIsDisabledContext();
	const CURRENT_FRAMES_LIMIT = FRAMES_LIMIT_V2;
	const hasReachedFrameLimit = frames?.length >= CURRENT_FRAMES_LIMIT;
	const [tabSelected, setTabSelected] = useState<TabValues>(TabValues.STYLES);
	const isTranslationEnabled = useIsTranslationSupportEnabled();

	const { setIsInViewMode, setSelectedToEditCardId } = useExtensionsActions();
	const [{ selectedToEditCardId }] = useExtensionsState();

	// Keep refs that always hold the latest values so we can read them inside
	// effects without listing them as dependencies (avoiding stale closures and
	// spurious re-runs when action-creator references change on each render).
	const selectedToEditCardIdRef = useRef(selectedToEditCardId);
	selectedToEditCardIdRef.current = selectedToEditCardId;
	const setSelectedToEditCardIdRef = useRef(setSelectedToEditCardId);
	setSelectedToEditCardIdRef.current = setSelectedToEditCardId;

	// Retain the open card across the manage-translations mount toggle.
	const savedCardIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (isTranslationEnabled) {
			if (isManageTranslationsOpen) {
				savedCardIdRef.current = selectedToEditCardIdRef.current;
			} else if (savedCardIdRef.current !== null) {
				setSelectedToEditCardIdRef.current(savedCardIdRef.current);
				savedCardIdRef.current = null;
			}
		}
	}, [isManageTranslationsOpen, isTranslationEnabled]);

	useEffect(() => {
		setIsInViewMode(false);
	}, [setIsInViewMode]);

	const handleAddFrameKeyPress = (event: KeyboardEvent) => {
		if (TRIGGER_BUTTON_KEYS.includes(event.key)) {
			setTimeout(() => {
				(framesListRef?.current?.lastChild as HTMLDivElement).focus();
			}, 100);
		}
	};

	const handleTabClick = useCallback((tab: { value: string }) => {
		setTabSelected(tab.value as TabValues);
	}, []);

	const renderConfigPanelFooter = useCallback(() => {
		const isContentTabSelected = tabSelected === TabValues.CONTENT;
		// when Carousel is disabled (ie. premium gated), don't show the footer
		if (isDisabled) {
			return null;
		}

		const showAddFramesButton = !hasReachedFrameLimit && isContentTabSelected;
		const showFramesErrorMessage = !frames?.length && isContentTabSelected;

		return (
			<Box xcss={styles.stickyFooter}>
				<Box xcss={styles.carouselListBuilderFooterWrapper}>
					{showAddFramesButton && (
						<AddItemButton
							addItem={addFrame}
							label={messages.addFrameText}
							onAddItemKeyPress={handleAddFrameKeyPress}
							analyticsSource={carouselConfigAnalyticSource}
							actionSubjectIdSuffix="Carousel"
						/>
					)}
					{showFramesErrorMessage && (
						<FormFieldErrorMessage>
							<FormattedMessage {...messages.framesInvalidMessage} />
						</FormFieldErrorMessage>
					)}
					{!isContentTabSelected && (
						<ConfigPanelFeedbackCollector
							source={carouselContentAnalyticSource}
							type={FeedbackType.CAROUSEL}
						/>
					)}
				</Box>
			</Box>
		);
	}, [frames, addFrame, isDisabled, hasReachedFrameLimit, tabSelected]);

	if (!carouselParameters) {
		return <NoExtensionData source={carouselExtensionKey} isConfigPanel />;
	}

	const updateFrames = (frames: Frame[]) => updateMainFormField({ cards: frames });

	const tabSections = [
		{
			label: formatMessage(messages.stylesTabLabel),
			value: TabValues.STYLES,
			tabContent: [
				{
					sectionId: 'general',
					title: formatMessage(messages.styleSectionGeneralTitle),
					defaultIsExpanded: true,
					component: (
						<CarouselConfigStylesSectionGeneral
							isCompanyHub={isCompanyHub}
							isAutoplayEnabled={isAutoplayEnabled}
							cornerStyle={cornerStyle}
							cornerRadius={cornerRadius}
							updateMainFormField={updateMainFormField}
							source={carouselConfigAnalyticSource}
						/>
					),
				},
				{
					sectionId: 'navigation',
					title: formatMessage(messages.styleSectionNavigationTitle),
					defaultIsExpanded: false,
					component: (
						<CarouselConfigStylesSectionNavigation
							updateMainFormField={updateMainFormField}
							navigationType={navigationStyle}
						/>
					),
				},
			],
		},
		{
			label: formatMessage(messages.contentTabLabel),
			value: TabValues.CONTENT,
			tabContent: [
				{
					sectionId: 'frames',
					component: (
						<Flex
							direction="column"
							gap="space.300"
							xcss={isDisabled && styles.disabledContentWrapper}
						>
							<FramesArea
								items={frames}
								itemsListRef={framesListRef}
								contentId={contentId}
								removeItem={removeFrame}
								updateFrameFormField={updateFrameFormField}
								updateItems={updateFrames}
								showBgAndTextColorOptions
								showMediaPicker
								showLayout
								shouldUseCardV2UI
							/>
						</Flex>
					),
				},
			],
		},
	];

	return (
		<>
			{!isManageTranslationsOpen && (
				<ExtensionsConfigWrapper
					title={<FormattedMessage {...messages.title} />}
					footerComponent={renderConfigPanelFooter}
					closeConfigPanel={closeConfigPanel}
				>
					<Box xcss={styles.supportLinkWrapper}>
						<FormattedMessage
							{...(showConfigPreambleSupportLink
								? messages.preambleV2
								: messages.preambleV2WithoutSupportLink)}
							values={{
								number: CURRENT_FRAMES_LIMIT,
								supportLink: (
									<Link target="_blank" href={SUPPORT_LINK}>
										<FormattedMessage {...messages.preambleSupportLinkLabelV2} />
									</Link>
								),
							}}
						/>
					</Box>
					<PremiumGateForExtensionConfigPanel feature="premiumCarouselConfig">
						<ConfigPanelGrouping
							source={carouselConfigAnalyticSource}
							tabSections={tabSections}
							onTabClick={handleTabClick}
							// Retain the selected tab across the manage-translations mount toggle (HUBS-1411).
							selected={tabSections.findIndex((tab) => tab.value === tabSelected)}
						/>
					</PremiumGateForExtensionConfigPanel>
				</ExtensionsConfigWrapper>
			)}
			{isManageTranslationsOpen && (
				<ManageTranslations
					parameters={carouselParameters}
					closeConfigPanel={closeConfigPanel}
					localId={localId}
					extensionType={carouselExtensionType}
					config={{
						fields: [
							{
								path: 'cards[].title',
								label: formatMessage(frameMessages.titleFieldLabel),
								placeholder: formatMessage(frameMessages.titlePlaceholder),
							},
							{
								path: 'cards[].description',
								label: formatMessage(frameMessages.descriptionFieldLabel),
								placeholder: formatMessage(frameMessages.descriptionPlaceholder),
							},
							{
								path: 'cards[].buttonText',
								label: formatMessage(messages.buttonTextLabel),
								placeholder: formatMessage(messages.buttonTextPlaceholder),
							},
						],
					}}
				/>
			)}
		</>
	);
};

export const CarouselConfigPanelContent = (
	props: CarouselConfigPanelContentProps,
): React.JSX.Element => (
	<ManageTranslationsOpenProvider>
		<CarouselConfigPanelContentInner {...props} />
	</ManageTranslationsOpenProvider>
);
